import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Check, Sparkles, ArrowRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import toast from 'react-hot-toast';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const cardRef = useRef(null);
  const UPLOADS_URL = import.meta.env.VITE_UPLOADS_URL;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsAdding(true);

    await new Promise(resolve => setTimeout(resolve, 300));

    addToCart(product, 1);
    toast.success(
      <div className="flex items-center gap-2">
        <Check className="w-4 h-4 text-green-500" />
        <span>Agregado al carrito</span>
      </div>,
      { duration: 2000 }
    );

    setTimeout(() => setIsAdding(false), 500);
  };

  const formatPrice = (price) => {
    const number = Math.round(Number(price) || 0);
    return '$' + number.toLocaleString('de-DE');
  };

  const getImageUrl = () => {
    if (product.primary_image_url) {
      if (product.primary_image_url.startsWith('http')) {
        return product.primary_image_url;
      }
      if (product.primary_image_url.startsWith('/ecommerce-api')) {
        return `https://decohomesinrival.com.ar${product.primary_image_url}`;
      }
      return `${UPLOADS_URL}${product.primary_image_url}`;
    }
    if (product.primary_image) {
      return `${UPLOADS_URL}${product.primary_image}`;
    }
    if (product.images && product.images.length > 0) {
      return `${UPLOADS_URL}${product.images[0].image_path}`;
    }
    if (product.image) {
      return product.image;
    }
    return null;
  };

  const originalPrice = parseFloat(product.price) || 0;
  const salePrice = product.sale_price ? parseFloat(product.sale_price) : 0;
  const hasDiscount = salePrice > 0 && salePrice < originalPrice;
  const discountedPrice = hasDiscount ? salePrice : originalPrice;

  const isNew = product.is_new || (product.created_at && isRecentProduct(product.created_at));
  const isFeatured = product.featured || product.is_featured;

  function isRecentProduct(createdAt) {
    const created = new Date(createdAt);
    const now = new Date();
    const diffDays = (now - created) / (1000 * 60 * 60 * 24);
    return diffDays <= 14;
  }

  return (
    <Link
      ref={cardRef}
      to={`/producto/${product.id}`}
      onClick={() => sessionStorage.setItem('scrollRestore', String(window.scrollY))}
      className={`group relative bg-white rounded-2xl overflow-hidden flex flex-col transition-all duration-300 ${isHovered ? '-translate-y-2' : ''}`}
      style={{
        boxShadow: isHovered
          ? '0 25px 50px -12px rgba(45, 60, 93, 0.25), 0 0 0 1px rgba(45, 60, 93, 0.08)'
          : '0 4px 20px -2px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.03)'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Contenedor de imagen */}
      <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Skeleton loader */}
        {!imageLoaded && getImageUrl() && (
          <div className="absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 animate-shimmer" />
        )}

        {getImageUrl() ? (
          <img
            src={getImageUrl()}
            alt={product.name}
            className={`w-full h-full object-cover transition-all duration-700 ease-out ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            } ${isHovered ? 'scale-110' : 'scale-100'}`}
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              e.target.style.display = 'none';
              setImageLoaded(true);
            }}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <svg className="w-16 h-16 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-xs text-gray-400 font-medium">Sin imagen</span>
          </div>
        )}

        {/* Overlay con gradiente en hover */}
        <div className={`absolute inset-0 bg-gradient-to-t from-[#2d3c5d]/80 via-transparent to-transparent transition-opacity duration-500 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`} />

        {/* Badges superiores - Solo renderizar si hay badges */}
        {(hasDiscount || isNew || (isFeatured && !isNew)) ? (
          <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
            {hasDiscount === true && (
              <div className="px-3 py-1.5 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-full text-xs font-bold shadow-lg shadow-red-500/30 flex items-center gap-1">
                <span>-{Math.round(((originalPrice - discountedPrice) / originalPrice) * 100)}%</span>
              </div>
            )}
            {isNew === true && (
              <div className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full text-xs font-bold shadow-lg shadow-emerald-500/30 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                <span>Nuevo</span>
              </div>
            )}
            {isFeatured === true && isNew !== true && (
              <div className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-xs font-bold shadow-lg shadow-amber-500/30 flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span>Destacado</span>
              </div>
            )}
          </div>
        ) : null}

        {/* Botón Ver Producto en hover */}
        <div className={`absolute bottom-3 left-3 right-3 transition-all duration-500 ${
          isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <div className="flex items-center justify-center gap-2 py-3 bg-white text-[#2d3c5d] rounded-xl font-semibold text-sm shadow-xl">
            <span>Ver producto</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

        {/* Indicador de sin stock */}
        {product.stock === 0 && (
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-20">
            <span className="px-4 py-2 bg-white rounded-full text-sm font-semibold text-gray-700 shadow-lg">
              Agotado
            </span>
          </div>
        )}
      </div>

      {/* Contenido */}
      <div className="p-4 flex flex-col flex-grow">
        {/* Categoría */}
        {product.category_name && (
          <span className="text-[10px] text-[#2d3c5d]/50 font-semibold uppercase tracking-wider mb-1.5">
            {product.category_name}
          </span>
        )}

        {/* Colores disponibles */}
        {product.available_colors?.length > 0 && (
          <div className="flex gap-1 mb-1.5">
            {product.available_colors.slice(0, 5).map((hex, i) => (
              <span
                key={i}
                className="w-3.5 h-3.5 rounded-full border border-gray-200"
                style={{ backgroundColor: hex }}
              />
            ))}
            {product.available_colors.length > 5 && (
              <span className="text-xs text-gray-400">+{product.available_colors.length - 5}</span>
            )}
          </div>
        )}

        {/* Nombre del producto */}
        <h3 className="text-[#2d3c5d] font-semibold text-sm sm:text-base leading-snug line-clamp-2 min-h-[2.5rem] group-hover:text-[#1f2a3d] transition-colors">
          {product.name}
        </h3>

        {/* Precio y botón */}
        <div className="mt-auto pt-4 flex items-end justify-between gap-3">
          <div className="flex flex-col">
            {hasDiscount && (
              <p className="text-gray-400 text-xs line-through">
                {formatPrice(originalPrice)}
              </p>
            )}
            <p className={`font-bold text-xl ${hasDiscount ? 'text-red-600' : 'text-[#2d3c5d]'}`}>
              {formatPrice(discountedPrice)}
            </p>
          </div>

          {/* MODO CATÁLOGO: Botón de agregar al carrito oculto */}
          {/* {product.stock !== 0 && (
            <button
              onClick={handleAddToCart}
              disabled={isAdding}
              className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 shadow-md ${
                isAdding
                  ? 'bg-green-500 text-white scale-95'
                  : 'bg-[#2d3c5d] text-white hover:bg-[#1f2a3d] hover:scale-105 active:scale-95'
              }`}
            >
              {isAdding ? (
                <Check className="w-5 h-5" />
              ) : (
                <ShoppingCart className="w-5 h-5" />
              )}
            </button>
          )} */}
        </div>
      </div>

      {/* Efecto de borde en hover */}
      <div className={`absolute inset-0 rounded-2xl transition-all duration-500 pointer-events-none ${
        isHovered
          ? 'ring-2 ring-[#2d3c5d]/10 ring-offset-2'
          : 'ring-0 ring-transparent'
      }`} />
    </Link>
  );
};

export default ProductCard;
